import { useState, useEffect } from 'react';
import UnsplashImage from '../components/UnsplashImage';
import { triggerDownload } from '../utils/unsplashUtils';
import { getAttributionUrl, getUnsplashUrl } from '../utils/unsplashUtils';

/**
 * Test page to demonstrate Unsplash API compliance
 * Share screenshots of this page with Unsplash for approval
 */
export default function UnsplashTest() {
  const [testPhoto, setTestPhoto] = useState(null);
  const [downloadCount, setDownloadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch a test image
    fetchTestImage();
  }, []);

  async function fetchTestImage() {
    setLoading(true);
    try {
      const response = await fetch(
        'https://api.unsplash.com/photos/random?query=judaism',
        {
          headers: {
            'Authorization': `Client-ID ${process.env.REACT_APP_UNSPLASH_ACCESS_KEY}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }
      
      const photo = await response.json();
      setTestPhoto(photo);
    } catch (error) {
      console.error('Error fetching test image:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleImageClick = async () => {
    if (testPhoto?.links?.download_location) {
      await triggerDownload(testPhoto.links.download_location);
      setDownloadCount(prev => prev + 1);
      alert('Download tracked! Check Unsplash dashboard for download count.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-serif font-bold mb-4">
          Unsplash API Compliance Test
        </h1>
        <p className="text-text-secondary mb-8">
          This page demonstrates proper attribution and download tracking.
        </p>

        {/* Test Image with Attribution */}
        {testPhoto && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h2 className="text-xl font-bold mb-4">Test Image with Attribution</h2>
              <div className="max-w-2xl">
                <UnsplashImage
                  photo={testPhoto}
                  alt="Test image from Unsplash"
                  className="w-full aspect-video object-cover rounded-xl"
                  onClick={handleImageClick}
                  triggerDownloadOnClick={true}
                />
              </div>
              
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold mb-2">Compliance Checklist:</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Attribution visible in bottom-right corner of image</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Photographer name is a clickable link with UTM parameters</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>"Unsplash" is a clickable link with UTM parameters</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Download endpoint triggered on click: {downloadCount} times</span>
                  </li>
                </ul>
              </div>

              {/* Show attribution URL for verification */}
              <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                <h3 className="font-semibold mb-2 text-sm">Attribution Links (for Unsplash review):</h3>
                <div className="space-y-1 text-xs font-mono break-all">
                  <p>
                    <strong>Photographer:</strong> {' '}
                    <a 
                      href={getAttributionUrl(testPhoto)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {getAttributionUrl(testPhoto)}
                    </a>
                  </p>
                  <p>
                    <strong>Unsplash:</strong> {' '}
                    <a 
                      href={getUnsplashUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {getUnsplashUrl()}
                    </a>
                  </p>
                  <p>
                    <strong>Download Location:</strong> {testPhoto.links?.download_location || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!testPhoto && !loading && (
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <p className="text-text-secondary">
              Could not load test image. Please check your Unsplash API key.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

